const { getElements } = window.BAO.Utils

class SizeGuide extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.conversion = this.dataset.conversion
    this.tableHeadingsFormat = this.dataset.headingsFormat
    this.variantsFormat = this.dataset.variantsFormat

    this.garmentTable = this.els?.garment?.element?.querySelector('table')
    this.bodyTable = this.els?.body?.element?.querySelector('table')
    this.sizeTable = this.els?.size?.element?.querySelector('table')
    this.drawer = this.externalEls?.drawer?.element

    this.handleInitialConversion()
    this.handleInitialHeadings()
    this.cacheUnitText()
    this.updateAllCellLabels()
    this.setInitialTab()

    if (this.hasAttribute('data-option')) {
      this.setDefaultColumns()
    }

    this.setupListeners()
  }

  cacheUnitText () {
    this.unitText = {}
    ;['inches', 'centimetres'].forEach(unit => {
      this.unitText[unit] = this.els?.[unit]?.element?.textContent.trim()
    })
  }

  updateAllCellLabels () {
    this.updateCellLabels(this.garmentTable)
    this.updateCellLabels(this.bodyTable)
    this.updateCellLabels(this.sizeTable)
  }

  updateCellLabels (table) {
    if (!table) return

    const rows = Array.from(table.rows)
    const columnHeaders = Array.from(rows[0]?.cells ?? [])
    const rowTitle = columnHeaders[0]?.textContent.trim()
    const textOf = el => el?.textContent.trim() ?? ''

    columnHeaders.slice(1).forEach(columnHeader => {
      columnHeader.removeAttribute('aria-label')
    })

    rows.slice(1).forEach(row => {
      const rowHeader = textOf(row.cells[0])

      Array.from(row.cells)
        .slice(1)
        .forEach((cell, index) => {
          const label = [
            [rowTitle, textOf(columnHeaders[index + 1])]
              .filter(Boolean)
              .join(' '),
            rowHeader,
          ]
            .filter(Boolean)
            .join(', ')

          if (label) {
            cell.setAttribute('aria-label', label)
          } else {
            cell.removeAttribute('aria-label')
          }

          cell.querySelector('[data-size-guide-unit]')?.remove()

          const unit = this.unitText?.[this.conversion]

          if (unit && cell.hasAttribute('data-centimetres')) {
            const unitEl = document.createElement('span')

            unitEl.className = 'util-ScreenReaderOnly'
            unitEl.setAttribute('data-size-guide-unit', '')
            unitEl.textContent = ` ${unit}`
            cell.appendChild(unitEl)
          }
        })
    })
  }

  setupListeners () {
    super.setupListeners()

    if (this.els?.trigger?.exists) {
      this.handleTrigger = this.handleTrigger.bind(this)

      this.els.trigger.elements.forEach(el => {
        this.listeners.add(el, 'click', this.handleTrigger)
      })
    }

    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)

    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)

    if (this.externalEls?.handle?.exists) {
      this.externalEls.handle.elements.forEach(handle => {
        this.listeners.add(handle, 'touchstart', this.onTouchStart)
        this.listeners.add(handle, 'touchmove', this.onTouchMove)
        this.listeners.add(handle, 'touchend', this.onTouchEnd)

        this.listeners.add(handle, 'mousedown', this.onMouseDown)
        this.listeners.add(document, 'mousemove', this.onMouseMove)
        this.listeners.add(document, 'mouseup', this.onMouseUp)
      })
    }

    this.onCellClick = this.onCellClick.bind(this)

    const addCellListeners = table =>
      getElements('td, th', { context: table }).forEach(el => {
        this.listeners.add(el, 'click', e => this.onCellClick(e, table))
        this.listeners.add(el, 'keydown', e => {
          if (e.key !== 'Enter' && e.key !== ' ') return

          e.preventDefault()
          this.onCellClick(e, table)
        })
      })

    addCellListeners(this.garmentTable)
    addCellListeners(this.bodyTable)
    addCellListeners(this.sizeTable)

    this.listeners.add(
      document,
      window.BAO.EVENTS.VARIANT.CHANGED,
      this.onVariantChanged.bind(this),
    )

    if (this.els?.inches?.exists) {
      this.onInchesClick = this.onInchesClick.bind(this)

      this.els.inches.elements.forEach(el => {
        this.listeners.add(el, 'click', this.onInchesClick)
      })
    }

    if (this.els?.centimetres?.exists) {
      this.onCentimetresClick = this.onCentimetresClick.bind(this)

      this.els.centimetres.elements.forEach(el => {
        this.listeners.add(el, 'click', this.onCentimetresClick)
      })
    }

    if (this.els?.select?.exists) {
      this.onSelectChange = this.onSelectChange.bind(this)

      this.els.select.elements.forEach(el => {
        this.listeners.add(el, 'change', this.onSelectChange)
      })
    }
  }

  onSelectChange () {
    const value = this.els.select.element.value

    getElements('tr:first-child :is(td, th)', {
      context: this.garmentTable,
    }).forEach(el => {
      if (el.cellIndex === 0) return

      const currentValue = el.dataset.value
      const currentObject = this.conversions[this.tableHeadingsFormat]
      if (!currentObject) return

      const valueIndex = currentObject.findIndex(
        el => String(el) === String(currentValue),
      )

      const newObject = this.conversions[value]
      if (!newObject) return

      const newValue = newObject[valueIndex]
      if (!newValue) return

      el.innerHTML = newValue
    })

    getElements('tr:first-child :is(td, th)', {
      context: this.bodyTable,
    }).forEach(el => {
      if (el.cellIndex === 0) return

      const currentValue = el.dataset.value
      const currentObject = this.conversions[this.tableHeadingsFormat]
      if (!currentObject) return

      const valueIndex = currentObject.findIndex(
        el => String(el) === String(currentValue),
      )

      const newObject = this.conversions[value]
      if (!newObject) return

      const newValue = newObject[valueIndex]
      if (!newValue) return

      el.innerHTML = newValue
    })

    this.updateAllCellLabels()
  }

  setInitialTab () {
    const activeTab = this.querySelector(
      '[data-size-guide-el="trigger"][aria-pressed="true"]',
    )

    if (!activeTab) return

    const activeTabValue = activeTab.dataset.tab

    this.setAttribute('data-tab', activeTabValue)
  }

  handleInitialConversion () {
    const handleConversion = el => {
      if (el.cellIndex === 0 || el.closest('tr').rowIndex === 0) return

      const centimetres = parseInt(el.innerHTML, 10)

      if (isNaN(centimetres)) return

      const inches = Math.round((centimetres / 2.54) * 10) / 10

      el.setAttribute('data-centimetres', centimetres)
      el.setAttribute('data-inches', inches)

      if (this.conversion === 'centimetres') {
        el.innerHTML = centimetres
      } else {
        el.innerHTML = inches
      }
    }

    getElements('td', { context: this.garmentTable }).forEach(el =>
      handleConversion(el),
    )

    getElements('td', { context: this.bodyTable }).forEach(el =>
      handleConversion(el),
    )
  }

  handleInitialHeadings () {
    const handleHeadings = el => {
      if (el.cellIndex === 0) return

      const value = el.innerHTML

      el.setAttribute('data-value', value)

      if (this.tableHeadingsFormat !== this.variantsFormat) {
        const currentValue = value
        const currentObject = this.conversions[this.tableHeadingsFormat]
        if (!currentObject) return

        const valueIndex = currentObject.findIndex(
          el => String(el) === String(currentValue),
        )

        const newObject = this.conversions[this.variantsFormat]
        if (!newObject) return

        const newValue = newObject[valueIndex]
        if (!newValue) return

        el.setAttribute('data-converted-value', newValue)
      }
    }

    getElements('tr:first-child :is(td, th)', {
      context: this.garmentTable,
    }).forEach(el => handleHeadings(el))

    getElements('tr:first-child :is(td, th)', {
      context: this.bodyTable,
    }).forEach(el => handleHeadings(el))

    if (
      this.els?.select?.exists &&
      this.tableHeadingsFormat !== this.variantsFormat
    ) {
      this.els.select.elements.forEach(el => {
        el.value = this.variantsFormat
        const event = new Event('change')
        el.dispatchEvent(event)
      })
    }
  }

  async onInchesClick () {
    this.convertToInches()

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [this.storageName]: 'inches',
        },
      }),
    })
  }

  async onCentimetresClick () {
    this.convertToCentimetres()

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [this.storageName]: 'centimetres',
        },
      }),
    })
  }

  convertToInches () {
    if (this.conversion === 'inches') return

    this.conversion = 'inches'

    this.els.inches.elements.forEach(el => {
      el.setAttribute('aria-current', true)
    })

    this.els.centimetres.elements.forEach(el => {
      el.setAttribute('aria-current', false)
    })

    getElements('td', { context: this.garmentTable }).forEach(el => {
      if (!el.hasAttribute('data-inches')) return

      el.innerHTML = el.dataset.inches
    })

    getElements('td', { context: this.bodyTable }).forEach(el => {
      if (!el.hasAttribute('data-inches')) return

      el.innerHTML = el.dataset.inches
    })

    this.updateAllCellLabels()
    this.updateImageLabels()
  }

  convertToCentimetres () {
    if (this.conversion === 'centimetres') return

    this.conversion = 'centimetres'

    this.els.inches.elements.forEach(el => {
      el.setAttribute('aria-current', false)
    })

    this.els.centimetres.elements.forEach(el => {
      el.setAttribute('aria-current', true)
    })

    getElements('td', { context: this.garmentTable }).forEach(el => {
      if (!el.hasAttribute('data-centimetres')) return

      el.innerHTML = el.dataset.centimetres
    })

    getElements('td', { context: this.bodyTable }).forEach(el => {
      if (!el.hasAttribute('data-centimetres')) return

      el.innerHTML = el.dataset.centimetres
    })

    this.updateAllCellLabels()
    this.updateImageLabels()
  }

  onCellClick (e, table) {
    const cellIndex = e?.currentTarget?.cellIndex

    if (cellIndex === 0 || !table) return

    table.classList.add('prd-SizeGuide_Table-sizeSelected')

    getElements('td, th', { context: table }).forEach(el =>
      el.classList.toggle(
        'prd-SizeGuide_Table-columnHighlight',
        el.cellIndex === cellIndex,
      ),
    )

    const matchingHeading = getElements('td, th', { context: table }).filter(
      el => el.closest('tr').rowIndex === 0 && el.cellIndex === cellIndex,
    )

    if (!matchingHeading.length) return

    let variantTitleStripped = matchingHeading[0]?.dataset?.value
      ?.trim()
      .toLowerCase()
    if (this.tableHeadingsFormat !== this.variantsFormat) {
      variantTitleStripped = matchingHeading[0]?.dataset?.convertedValue
        ?.trim()
        .toLowerCase()
    }

    let optionSelector
    if (this.dataset.context === 'product-quick-buy') {
      optionSelector =
        '.qb-Detail[data-context="product-quick-buy"] [data-variant-radios-el="option-choice"], .prd-Detail [data-variant-radios-el="option-choice"]'
    } else if (this.dataset.context === 'quick-buy') {
      optionSelector =
        '.qb-Detail[data-context="quick-buy"] [data-variant-radios-el="option-choice"]'
    }

    const matchingOptions = Array.from(
      document.querySelectorAll(optionSelector),
    ).filter(
      el =>
        el?.dataset.shortValue?.trim()?.toLowerCase() === variantTitleStripped,
    )

    if (matchingOptions.length) {
      matchingOptions.forEach(option => option.click())
    }

    this.updateImageLabels()
  }

  updateImageLabels () {
    const updateLabels = (el, type) => {
      if (el.rowIndex === 0) return

      const label = el?.cells[0]?.innerText?.toLowerCase()?.trim()

      const valueCell =
        el?.querySelector('.prd-SizeGuide_Table-columnHighlight') ||
        el?.querySelector('td:only-of-type')
      const valueClone = valueCell?.cloneNode(true)

      valueClone?.querySelector('[data-size-guide-unit]')?.remove()

      const value = valueClone?.innerHTML

      const potentialImageLabel = this.querySelector(
        `[data-size-guide-el="${type}-label"][data-label="${label}"]`,
      )

      if (!potentialImageLabel) return

      potentialImageLabel.innerHTML = value
    }

    getElements('tr', { context: this.garmentTable }).forEach(el =>
      updateLabels(el, 'garment'),
    )

    getElements('tr', { context: this.bodyTable }).forEach(el =>
      updateLabels(el, 'body'),
    )
  }

  setDefaultColumns () {
    const option = this.dataset.option

    this.findMatchingColumn(option, this.garmentTable)
    this.findMatchingColumn(option, this.bodyTable)
    this.findMatchingColumn(option, this.sizeTable)

    this.updateImageLabels()
  }

  onVariantChanged (e) {
    const option = e?.detail?.event?.target?.dataset?.shortValue

    this.findMatchingColumn(option, this.garmentTable)
    this.findMatchingColumn(option, this.bodyTable)
    this.findMatchingColumn(option, this.sizeTable)

    this.updateImageLabels()
  }

  findMatchingColumn (option, table) {
    const variantTitle = option.trim()
    const variantTitleStripped = variantTitle.toLowerCase()

    if (!table) return

    let cellIndex = getElements('td, th', { context: table }).findIndex(
      el => el?.dataset?.value?.trim().toLowerCase() === variantTitleStripped,
    )
    if (this.tableHeadingsFormat !== this.variantsFormat) {
      cellIndex = getElements('td, th', { context: table }).findIndex(
        el =>
          el?.dataset?.convertedValue?.trim().toLowerCase() ===
          variantTitleStripped,
      )
    }

    if (cellIndex === -1) return

    table.classList.add('prd-SizeGuide_Table-sizeSelected')

    getElements('th, td', { context: table }).forEach(el =>
      el.classList.toggle(
        'prd-SizeGuide_Table-columnHighlight',
        el.cellIndex === cellIndex,
      ),
    )
  }

  handleTrigger (event) {
    const triggerEl = event.target.closest(this.els?.trigger?.selector)

    this.els.panel.elements.forEach(panelEl => {
      panelEl.setAttribute(
        'aria-hidden',
        panelEl.dataset.tab !== triggerEl.dataset.tab,
      )
    })

    this.els.trigger.elements.forEach(trigger => {
      trigger.setAttribute(
        'aria-pressed',
        trigger.dataset.tab === triggerEl.dataset.tab,
      )
    })

    this.setAttribute('data-tab', triggerEl.dataset.tab)
  }

  onTouchStart (event) {
    this.touchstartY = event.changedTouches[0].screenY

    this.setAttribute('drag', '')
  }

  onTouchMove (event) {
    const diffY = event.changedTouches[0].screenY - this.touchstartY

    if (diffY <= 0) return

    this.drawer.style.setProperty('--Drag', `${diffY}px`)
  }

  onTouchEnd (event) {
    this.touchendY = event.changedTouches[0].screenY

    this.style.setProperty('--Drag', `0px`)

    this.removeAttribute('drag')

    if (this.touchendY > this.touchstartY) {
      document
        .querySelector(
          `.drw-Drawer-active [data-module-drawers-close^="size-guide"]`,
        )
        .click()
    }
  }

  onMouseDown (event) {
    if (this.isTouch === true) return

    this.mouseDown = true

    this.touchstartY = event.screenY

    this.setAttribute('drag', '')
  }

  onMouseMove (event) {
    if (this.isTouch === true) return

    if (this.mouseDown === false) return

    const diffY = event.screenY - this.touchstartY

    if (diffY <= 0) return

    this.drawer.style.setProperty('--Drag', `${diffY}px`)
  }

  onMouseUp (event) {
    if (this.isTouch === true) return

    if (this.mouseDown === false) return

    this.mouseDown = false

    this.touchendY = event.screenY

    this.style.setProperty('--Drag', `0px`)

    this.removeAttribute('drag')

    if (this.touchendY > this.touchstartY) {
      document
        .querySelector(
          `.drw-Drawer-active [data-module-drawers-close^="size-guide"]`,
        )
        .click()
    }
  }

  get storageName () {
    return 'size_guide_conversion'
  }

  get conversions () {
    return {
      UK: ['4', '6', '8', '10', '12', '14', '16'],
      SML: ['S', 'S', 'M', 'M', 'L', 'L', 'XL'],
      IT: ['36', '38', '40', '42', '44', '46', '48'],
      US: ['0', '2', '4', '6', '8', '10', '12'],
      FR: ['32', '34', '36', '38', '40', '42', '44'],
      DE: ['30', '32', '34', '36', '38', '40', '42'],
      JP: ['3', '5', '7', '9', '11', '13', '15'],
      DK: ['30', '32', '34', '36', '38', '40', '42'],
      AU: ['4', '6', '8', '10', '12', '14', '16'],
      KR: ['33', '44', '55', '66', '77', '88', '99'],
      'CN Bottoms': [
        '150/54A',
        '155/58A',
        '160/62A',
        '165/66A',
        '170/70A',
        '175/74A',
        '180/96A',
      ],
      'CN Dress/Top': [
        '150/72A',
        '155/76A',
        '160/80A',
        '165/84A',
        '170/92A',
        '175/94A',
        '180/96A',
      ],
    }
  }
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide)
}
